// Instalación requerida:
// npm install @react-pdf/renderer

import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
  Font
} from '@react-pdf/renderer';
import { PropertyListing } from '../../../../types/Property';

// Registrar fuentes si es necesario
Font.register({
  family: 'Roboto',
  src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf'
});

// Estilos para el PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 20,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 20,
    textAlign: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#228B22',
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 12,
    color: '#555555',
    marginBottom: 5,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#228B22',
    marginVertical: 10,
  },
  propertyContainer: {
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 5,
    padding: 10,
  },
  propertyHeader: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#228B22',
    marginBottom: 10,
  },
  propertyContent: {
    flexDirection: 'row',
    gap: 10,
  },
  imageContainer: {
    width: 120,
    height: 80,
    marginRight: 10,
  },
  propertyImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 3,
  },
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 3,
  },
  placeholderText: {
    fontSize: 8,
    color: '#999',
    textAlign: 'center',
  },
  infoContainer: {
    flex: 1,
    paddingLeft: 5,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 4,
    alignItems: 'center',
  },
  infoIcon: {
    width: 12,
    fontSize: 8,
    color: '#228B22',
    fontWeight: 'bold',
    marginRight: 5,
  },
  infoText: {
    fontSize: 9,
    color: '#333',
    flex: 1,
  },
  descriptionContainer: {
    marginTop: 8,
    paddingTop: 5,
    borderTopWidth: 0.5,
    borderTopColor: '#eee',
  },
  descriptionTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 3,
    color: '#228B22',
  },
  descriptionText: {
    fontSize: 8,
    color: '#555',
    lineHeight: 1.3,
  },
  servicesContainer: {
    marginTop: 5,
  },
  servicesTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    marginBottom: 2,
    color: '#228B22',
  },
  servicesText: {
    fontSize: 7,
    color: '#666',
    lineHeight: 1.2,
  },
  statsContainer: {
    marginTop: 20,
    padding: 15,
    backgroundColor: '#f9f9f9',
    borderRadius: 5,
  },
  statsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#228B22',
    marginBottom: 10,
    textAlign: 'center',
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  statItem: {
    width: '48%',
    marginBottom: 8,
  },
  statLabel: {
    fontSize: 8,
    color: '#666',
    marginBottom: 2,
  },
  lineThrough: {
    textDecoration: 'line-through',
  },
  statValue: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#333',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    right: 20,
    textAlign: 'center',
    fontSize: 8,
    color: '#999',
    borderTopWidth: 0.5,
    borderTopColor: '#ddd',
    paddingTop: 5,
  },
  pageNumber: {
    position: 'absolute',
    bottom: 10,
    right: 20,
    fontSize: 8,
    color: '#666',
  }
});



// Componente de una propiedad individual
const PropertyItem: React.FC<{ property: PropertyListing; index: number }> = ({
  property,
  index
}) => (
  <View style={styles.propertyContainer}>
    <Text style={styles.propertyHeader}>
      {index + 1}. {property.title || 'Sin título'}
    </Text>

    <View style={styles.propertyContent}>
      {/* Imagen */}
      <View style={styles.imageContainer}>
        {property.photos && property.photos.length > 0 && property.photos[0] ? (
          <Image
            style={styles.propertyImage}
            src={property.photos[0]}
            cache={false}
          />
        ) : (
          <View style={styles.imagePlaceholder}>
            <Text style={styles.placeholderText}>Sin imagen{'\n'}disponible</Text>
          </View>
        )}
      </View>

      {/* Información */}
      <View style={styles.infoContainer}>
        <View style={styles.infoRow}>
          <Text style={styles.infoIcon}>🏠</Text>
          <Text style={styles.infoText}>
            Categoría: {property.categoryName || 'N/A'}
          </Text>
        </View>

        {property.coupon && (
          <View style={styles.infoRow}>
            <Text style={styles.infoIcon}>🎟️</Text>
            <Text style={styles.infoText}>
              Cupón: {property.coupon.title.toUpperCase() || 'N/A'}
            </Text>
          </View>
        )}

        {property.promotion ? (
          <>
            <View style={styles.infoRow}>
              <Text style={styles.infoIcon}>💰</Text>
              <Text style={styles.infoText}>
                Precio original: <Text style={styles.lineThrough}>{property.promotion?.oldPrice
                  ? `${property.currency || 'CRC'} ${property.promotion.oldPrice.toLocaleString()}`
                  : 'No especificado'}</Text>
              </Text>
            </View>

            <View style={styles.infoRow}>
              <Text style={styles.infoIcon}>💰</Text>
              <Text style={styles.infoText}>
                Precio con descuento: {property.price
                  ? `${property.currency || 'CRC'} ${property.price.toLocaleString()}`
                  : 'No especificado'}
              </Text>
            </View>
          </>
        ) : <View style={styles.infoRow}>
          <Text style={styles.infoIcon}>💰</Text>
          <Text style={styles.infoText}>
            Precio original: {property.price
              ? `${property.currency || 'CRC'} ${property.price.toLocaleString()}`
              : 'No especificado'}
          </Text>
        </View>
        }

        <View style={styles.infoRow}>
          <Text style={styles.infoIcon}>📐</Text>
          <Text style={styles.infoText}>
            Dimensión: {property.dimension || 'N/A'} m²
          </Text>
        </View>

        <View style={styles.infoRow}>
          <Text style={styles.infoIcon}>📅</Text>
          <Text style={styles.infoText}>
            Fecha: {property.createdDate
              ? new Date(property.createdDate).toLocaleDateString('es-ES')
              : 'N/A'}
          </Text>
        </View>

        <View style={styles.infoRow}>
          <Text style={styles.infoIcon}>📅</Text>
          <Text style={styles.infoText}>
            Dirección: {property.address_Description || 'N/A'}
          </Text>
        </View>


        {/* Descripción */}
        {property.description && (
          <View style={styles.descriptionContainer}>
            <Text style={styles.descriptionTitle}>Descripción:</Text>
            <Text style={styles.descriptionText}>
              {property.description.length > 200
                ? property.description.substring(0, 200) + '...'
                : property.description}
            </Text>
          </View>
        )}

        {/* Servicios y Amenidades */}
        {(property.services || property.amenities) && (
          <View style={styles.servicesContainer}>
            {property.services && (
              <View>
                <Text style={styles.servicesTitle}>🔧 Servicios:</Text>
                <Text style={styles.servicesText}>
                  {property.services.length > 100
                    ? property.services.substring(0, 100) + '...'
                    : property.services}
                </Text>
              </View>
            )}

            {property.amenities && (
              <View style={{ marginTop: 3 }}>
                <Text style={styles.servicesTitle}>⭐ Amenidades:</Text>
                <Text style={styles.servicesText}>
                  {property.amenities.length > 100
                    ? property.amenities.substring(0, 100) + '...'
                    : property.amenities}
                </Text>
              </View>
            )}
          </View>
        )}
      </View>
    </View>
  </View>
);

// Componente principal del documento PDF
const CampaignPDFDocument: React.FC<{
  properties: PropertyListing[];
  dateFilter: string;
}> = ({ properties, dateFilter }) => {
  const currentDate = new Date().toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Calcular estadísticas
  const totalValue = properties.reduce((sum, prop) => sum + (prop.price || 0), 0);
  const avgPrice = properties.length > 0 ? totalValue / properties.length : 0;
  const propertiesWithPrice = properties.filter(p => p.price && p.price > 0).length;

  // Agrupar por categoría
  const categoryCounts = properties.reduce((acc, prop) => {
    const category = prop.categoryName || 'Sin categoría';
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Dividir propiedades en páginas (aproximadamente 3 por página)
  const propertiesPerPage = 3;
  const totalPages = Math.ceil(properties.length / propertiesPerPage);
  const propertyPages = Array.from({ length: totalPages }, (_, i) =>
    properties.slice(i * propertiesPerPage, (i + 1) * propertiesPerPage)
  );

  return (
    <Document>
      {/* Páginas de propiedades */}
      {propertyPages.map((pageProperties, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page}>
          {/* Header solo en la primera página */}
          {pageIndex === 0 && (
            <View style={styles.header}>
              <Text style={styles.title}>CAMPAÑA DE PROPIEDADES</Text>
              <Text style={styles.subtitle}>Filtro aplicado: {dateFilter}</Text>
              <Text style={styles.subtitle}>Fecha de generación: {currentDate}</Text>
              <Text style={styles.subtitle}>Total de propiedades: {properties.length}</Text>
              <View style={styles.divider} />
            </View>
          )}

          {/* Propiedades de esta página */}
          {pageProperties.map((property, index) => (
            <PropertyItem
              key={property.id_Property}
              property={property}
              index={pageIndex * propertiesPerPage + index}
            />
          ))}

          {/* Footer */}
          <Text style={styles.footer}>
            Generado por Alien Realty - {new Date().toLocaleDateString('es-ES')}
          </Text>
          <Text style={styles.pageNumber}>
            Página {pageIndex + 1} de {totalPages + 1}
          </Text>
        </Page>
      ))}

      {/* Página de estadísticas */}
      <Page size="A4" style={styles.page}>
        <View style={styles.statsContainer}>
          <Text style={styles.statsTitle}>RESUMEN ESTADÍSTICO</Text>

          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <Text style={styles.statLabel}>Valor Total del Portafolio</Text>
              <Text style={styles.statValue}>CRC {totalValue.toLocaleString()}</Text>
            </View>

            <View style={styles.statItem}>
              <Text style={styles.statLabel}>Precio Promedio</Text>
              <Text style={styles.statValue}>CRC {Math.round(avgPrice).toLocaleString()}</Text>
            </View>

            <View style={styles.statItem}>
              <Text style={styles.statLabel}>Propiedades con Precio</Text>
              <Text style={styles.statValue}>{propertiesWithPrice} de {properties.length}</Text>
            </View>

            <View style={styles.statItem}>
              <Text style={styles.statLabel}>Porcentaje con Precio</Text>
              <Text style={styles.statValue}>
                {Math.round((propertiesWithPrice / properties.length) * 100)}%
              </Text>
            </View>
          </View>

          {/* Distribución por categorías */}
          <View style={{ marginTop: 20 }}>
            <Text style={[styles.descriptionTitle, { fontSize: 12, marginBottom: 10 }]}>
              Distribución por Categorías:
            </Text>
            {Object.entries(categoryCounts).map(([category, count]) => {
              const percentage = Math.round((count / properties.length) * 100);
              return (
                <View key={category} style={styles.infoRow}>
                  <Text style={styles.infoText}>
                    • {category}: {count} propiedades ({percentage}%)
                  </Text>
                </View>
              );
            })}
          </View>
        </View>

        <Text style={styles.footer}>
          Generado por Alien Realty - {new Date().toLocaleDateString('es-ES')}
        </Text>
        <Text style={styles.pageNumber}>
          Página {totalPages + 1} de {totalPages + 1}
        </Text>
      </Page>
    </Document>
  );
};

// Función para generar PDF con vista previa
export const generateReactPDFWithPreview = async (
  properties: PropertyListing[],
  dateFilter: string
): Promise<void> => {
  try {
    console.log(`🔄 Generando PDF con ${properties.length} propiedades...`);

    const doc = <CampaignPDFDocument properties={properties} dateFilter={dateFilter} />;

    const blob = await pdf(doc).toBlob();

    const url = URL.createObjectURL(blob);

    const newWindow = window.open(url, '_blank');

    if (newWindow) {
      newWindow.document.title = `Campaña de Propiedades - ${new Date().toLocaleDateString('es-ES')}`;

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60000);

      console.log('✅ PDF abierto en nueva ventana');
    } else {
      // Fallback: descargar si se bloquea el popup
      const link = document.createElement('a');
      link.href = url;
      link.download = `campana-propiedades-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('✅ PDF descargado (popup bloqueado)');
    }

  } catch (error) {
    console.error('❌ Error generando PDF:', error);
    throw new Error(`Error al generar PDF: ${error instanceof Error ? error.message : 'Error desconocido'}`);
  }
};

// Función para descarga directa
export const downloadReactPDFDirectly = async (
  properties: PropertyListing[],
  dateFilter: string
): Promise<void> => {
  try {
    console.log(`🔄 Descargando PDF con ${properties.length} propiedades...`);

    const doc = <CampaignPDFDocument properties={properties} dateFilter={dateFilter} />;
    const blob = await pdf(doc).toBlob();

    // Crear link de descarga
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `campana-propiedades-${new Date().toISOString().split('T')[0]}.pdf`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Limpiar URL
    URL.revokeObjectURL(url);

    console.log('✅ PDF descargado exitosamente');

  } catch (error) {
    console.error('❌ Error descargando PDF:', error);
    throw new Error(`Error al descargar PDF: ${error instanceof Error ? error.message : 'Error desconocido'}`);
  }
};

export const availableTags = [
    { value: 'tag_red', label: 'Rojo', color: '#f44336' },
    { value: 'tag_black_white', label: 'Blanco y Negro', color: '#424242' },
    { value: 'tag_celeste', label: 'Celeste', color: '#03a9f4' },
    { value: 'tag_cyan', label: 'Cyan', color: '#00bcd4' },
    { value: 'tag_green', label: 'Verde', color: '#4caf50' },
    { value: 'tag_purple', label: 'Púrpura', color: '#9c27b0' },
    { value: 'tag_turquoise', label: 'Turquesa', color: '#009688' },
    { value: 'tag_yellow', label: 'Amarillo', color: '#ffeb3b' },
  ];